import {Box, Stack, Typography, capitalize} from '@mui/material';
import React from 'react';

import {DataInfo, PrintTemplateContainer, ProductLogo, StyledGrid} from 'presentation/components';
import {
	AppointmentInstructions,
	ConfirmationCard,
} from 'presentation/pages/private/booking/components';
import {SquareLabelTime} from 'presentation/pages/private/booking/components/SquareLabelTime';
import {TELEMEDICINE_INFO} from 'presentation/pages/private/booking/constants';

import {translations} from 'presentation/translations-new';
import {formatMasks} from 'presentation/utils';
import {usePrintTemplate} from './hooks/usePrintTemplate';
import {PrintTemplateProps} from './types';
import {Attachments} from '../Attachments';

const {
	at,
	attendanceUnit,
	phones,
	professionalAndDate,
	protocolNumber: protocolNumberText,
	attendance,
} = translations['pt-br'].commons;
const {appointmentSummary} = translations['pt-br'].privateFlow.bookingFlow;
const {
	myAppointmentDetails: {appointmentInstructions},
	uploadAttachments,
} = translations['pt-br'].privateFlow.myAppointments;
const {appointmentCity} = translations['pt-br'].privateFlow.profile;

export function PrintTemplate({bookedAppointment, flowType, printRef, files}: PrintTemplateProps) {
	const {
		isMobile,
		isTablet,
		iconName,
		colors,
		isTelehealth,
		clinicName,
		formattedAddress,
		formattedDateWithWeekday,
		hour,
		phoneNumbers,
		providerName,
		specialtyName,
		protocolNumber,
		showAddress,
		showProtocolCard,
		day,
		monthName,
		isUploadFileTeleFeatureFlagEnabled,
		isAdvanceDiagnosticsExam,
	} = usePrintTemplate({bookedAppointment, flowType});

	return (
		<PrintTemplateContainer printRef={printRef}>
			<ProductLogo px={isMobile ? 2 : 4} />
			<StyledGrid.Root p={isMobile ? 2 : 4}>
				<StyledGrid.Item sm={12} md={12} lg={8}>
					<Stack gap={2}>
						<Typography variant="subtitle2" color={colors.light.text.secondary}>
							{appointmentSummary}
						</Typography>
						<ConfirmationCard.Root>
							<ConfirmationCard.Header subheader={professionalAndDate}>
								<Stack flexDirection={'row'} gap={2} alignItems={'start'}>
									<SquareLabelTime day={day} month={monthName} />
									<Stack width={'100%'}>
										<Typography variant="subtitle2" fontSize={'1.125rem'}>
											{`${capitalize(formattedDateWithWeekday)} ${at} ${hour}`}
										</Typography>
										{isAdvanceDiagnosticsExam ? null : (
											<ConfirmationCard.Content
												iconName="medical-services-filled"
												primaryText={providerName}
												height={'28px'}
											/>
										)}
										<ConfirmationCard.Content
											iconName={iconName}
											primaryText={specialtyName}
											height={'28px'}
										/>
									</Stack>
								</Stack>
							</ConfirmationCard.Header>
						</ConfirmationCard.Root>
						<Box
							display="grid"
							gridTemplateColumns={isTablet ? '1fr' : '1fr 1fr'}
							gap={2}>
							<ConfirmationCard.Root>
								<ConfirmationCard.Header
									subheader={isTelehealth ? attendance : attendanceUnit}
								/>
								<Stack
									flexDirection={'column'}
									justifyContent="space-between"
									gap={1}>
									<ConfirmationCard.Content
										iconName={
											isTelehealth
												? 'remember-me-filled'
												: 'location-on-filled'
										}
										primaryText={
											isTelehealth ? TELEMEDICINE_INFO.name : clinicName
										}
										secondaryText={isTelehealth ? '' : formattedAddress}
									/>
								</Stack>
							</ConfirmationCard.Root>
							{isTelehealth ? (
								<ConfirmationCard.Root>
									<ConfirmationCard.Header
										subheader={appointmentInstructions.appointmentProtocol}
									/>
									<ConfirmationCard.Content
										iconName={'assignment-filled'}
										primaryText={protocolNumberText}
										secondaryText={protocolNumber}
									/>
								</ConfirmationCard.Root>
							) : (
								<ConfirmationCard.Root>
									<ConfirmationCard.Header subheader={appointmentCity} />
									<ConfirmationCard.Content
										iconName={'my-location-filled'}
										primaryText={showAddress().first}
										secondaryText={showAddress().second}
									/>
								</ConfirmationCard.Root>
							)}
						</Box>
						{showProtocolCard ? (
							<ConfirmationCard.Root>
								<ConfirmationCard.Header
									subheader={appointmentInstructions.appointmentProtocol}
								/>
								<ConfirmationCard.Content
									iconName={'assignment-filled'}
									primaryText={protocolNumberText}
									secondaryText={protocolNumber}
								/>
							</ConfirmationCard.Root>
						) : null}

						{isUploadFileTeleFeatureFlagEnabled && isTelehealth ? (
							<>
								<Typography variant="subtitle2" color={colors.light.text.secondary}>
									{uploadAttachments}
								</Typography>
								<Attachments
									files={files ? files : []}
									attendanceId={bookedAppointment.bookingNumber}
									showUpload={false}
									acceptAttachment={bookedAppointment.acceptsAttachment}
								/>
							</>
						) : null}

						{phoneNumbers && phoneNumbers.length !== 0 ? (
							<ConfirmationCard.Root>
								<ConfirmationCard.Header subheader={phones} />
								<Stack direction="row" spacing={1}>
									{phoneNumbers.map((phoneNumber, index) => (
										<DataInfo
											key={index}
											primaryText={formatMasks('phone', phoneNumber)}
											listItemProps={{sx: {p: 0}}}
										/>
									))}
								</Stack>
							</ConfirmationCard.Root>
						) : null}
					</Stack>
				</StyledGrid.Item>

				<StyledGrid.Item sm={12} md={12} lg={4}>
					<AppointmentInstructions hideAction appointment={bookedAppointment} />
				</StyledGrid.Item>
			</StyledGrid.Root>
		</PrintTemplateContainer>
	);
}
